import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';

const STORAGE_KEY = 'klok-theme';

const THEME_MODES = {
  light: {
    algorithm: antdTheme.defaultAlgorithm,
    tokens: {
      colorPrimary: '#E0701A',
      colorInfo: '#3B6B8F',
      colorSuccess: '#3F7D58',
      colorWarning: '#B5821A',
      colorError: '#B5462E',
      colorText: '#2A2722',
      colorTextSecondary: '#756E62',
      colorBgBase: '#FAF8F5',
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E7E2DA',
      colorBorderSecondary: '#F3F0EB',
      borderRadius: 9,
      fontFamily: "'Inter', sans-serif",
      fontSize: 12,
      controlHeight: 32,
      controlOutline: 'rgba(224,112,26,.16)',
    },
  },
  dark: {
    algorithm: antdTheme.darkAlgorithm,
    tokens: {
      colorPrimary: '#F4A24C',
      colorInfo: '#7EA7C4',
      colorSuccess: '#7DB892',
      colorWarning: '#D7AE52',
      colorError: '#D77A62',
      colorText: '#F4F0EA',
      colorTextSecondary: '#C8BFB2',
      colorBgBase: '#14120F',
      colorBgContainer: '#211E1A',
      colorBorder: '#3A342C',
      colorBorderSecondary: '#2A2722',
      borderRadius: 9,
      fontFamily: "'Inter', sans-serif",
      fontSize: 12,
      controlHeight: 32,
      controlOutline: 'rgba(244,162,76,.2)',
    },
  },
};

const KlokThemeContext = createContext({
  themeMode: 'light',
  setThemeMode: () => {},
  toggleThemeMode: () => {},
});

const getInitialThemeMode = () => {
  if (typeof window === 'undefined') return 'light';

  const storedTheme = window.localStorage.getItem(STORAGE_KEY);
  return storedTheme === 'dark' ? 'dark' : 'light';
};

export const KlokThemeProvider = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(getInitialThemeMode);

  const setThemeMode = (mode) => {
    setThemeModeState(mode === 'dark' ? 'dark' : 'light');
  };

  const toggleThemeMode = () => {
    setThemeModeState((current) => (current === 'dark' ? 'light' : 'dark'));
  };

  useEffect(() => {
    document.documentElement.dataset.theme = themeMode;
    window.localStorage.setItem(STORAGE_KEY, themeMode);
  }, [themeMode]);

  const value = useMemo(
    () => ({
      themeMode,
      setThemeMode,
      toggleThemeMode,
    }),
    [themeMode]
  );

  const config = THEME_MODES[themeMode] || THEME_MODES.light;

  return (
    <KlokThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: config.algorithm,
          token: config.tokens,
          components: {
            Button: {
              borderRadius: 9,
              controlHeight: 32,
              fontWeight: 600,
              primaryShadow: '0 3px 10px rgba(224,112,26,.25)',
            },
            Card: {
              borderRadiusLG: 16,
              paddingLG: 20,
            },
            Drawer: {
              borderRadiusLG: 16,
            },
            Input: {
              borderRadius: 9,
              activeBorderColor: config.tokens.colorPrimary,
              hoverBorderColor: config.tokens.colorPrimary,
            },
            Modal: {
              borderRadiusLG: 16,
            },
            Select: {
              borderRadius: 9,
              optionSelectedBg: themeMode === 'dark' ? '#3A2B21' : '#FBF1E7',
            },
            Tooltip: {
              // one tooltip look for the whole app - screens must not restyle it
              colorBgSpotlight: themeMode === 'dark' ? '#2F2A24' : '#2A2722',
              colorTextLightSolid: '#FDFBF8',
              borderRadius: 8,
              borderRadiusOuter: 8,
              controlHeight: 28,
              fontSize: 12,
              lineHeight: 1.5,
              paddingSM: 10,
              paddingXS: 8,
              sizePopupArrow: 12,
              boxShadowSecondary: themeMode === 'dark'
                ? '0 6px 18px rgba(0,0,0,.55)'
                : '0 6px 18px rgba(42,39,34,.22)',
            },
            Table: {
              borderColor: config.tokens.colorBorderSecondary,
              headerBg: themeMode === 'dark' ? '#211E1A' : '#FAF8F5',
              headerColor: themeMode === 'dark' ? '#C8BFB2' : '#756E62',
              rowHoverBg: themeMode === 'dark' ? '#2A241E' : '#FBF1E7',
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </KlokThemeContext.Provider>
  );
};

export const useKlokTheme = () => useContext(KlokThemeContext);
